import { createSelector } from '@ngrx/store'
import { eventStateSelector, JoiEvent, WFWorker } from '../events.era'
import { myGroupsReselector } from './workforce.reselector';
import { TimeLogSummary, Task, filterTimeTasks, taskStatusNames } from 'globals/store/tasks.data';
import { Dictionary } from '@ngrx/entity';
import { TimeLogData, TaskTimeTableData, TaskTableData } from './../../helpers/table-column-types';
import { User } from 'globals/store/user.data';
import { TableUser } from './../../helpers/table.data';
import { FilterGroup, FilterGroupTypes, FiltersState, filterStateSelector } from '../filters.era';
import { filterTasks } from './filteredTasks.reselector';
import { DayFull } from 'src/app/helpers/constants';
import { JoiAccount } from 'globals/store/accounts.data';
import { Label } from 'globals/store/events.data';
import publicHelpers from 'src/app/helpers/public-helpers';
import { allDefaultTaskColumns, allTaskColumnsById, defaultTaskColumns, tableLightColors, taskColumnsById } from 'src/app/helpers/table-helpers';
import { columnOrderTypes, settingsStateSelector } from '../settings.era';
import { filteredCalendarEventsReselectorGenerator } from './filteredEvents.reselector';
import { AccountPublicInfo } from 'globals/store/accounts.data';
import { getWorkersByEmailAndUidFromMultipleEvents, WorkerWithProfile } from 'globals/store/workforce.data';
import { entitiesReselector } from "./entity.reselector";
import { userStateSelector } from '../user.era';
import { permissionsStateSelector } from '../permissions.era';
import { tasksStateSelector } from '../tasks.era';
import { workforceStateSelector } from '../workforce.era';
import { eventPreferencesStateSelector } from '../event.preferences.era';
import { accountStateSelector } from '../accounts.era';
import { makeMapFromListOfObjectsAndKey, minutesToHHMM, str2Date } from 'globals/helpers/helpers';
import { nameJoiner } from 'src/app/helpers/helpers';

const user = createSelector(userStateSelector, (state) => state.user)
const permissions = createSelector(permissionsStateSelector, (state) => state.access.types)
const all_triggers = createSelector(tasksStateSelector, (state) => state.triggers)
const focused = createSelector(tasksStateSelector, (state) => state.focused)
const tasks = createSelector(tasksStateSelector, (state) => state.entities)
const filters = createSelector(filterStateSelector, (state) => state)
const workersByEmail = createSelector(workforceStateSelector, (state) => state.workersByEmail)
const workersByUId = createSelector(workforceStateSelector, (state) => state.workersByUId)
const taskLabels = createSelector(eventStateSelector, (state) => state.labelMaps.task_labels)
const userSettings = createSelector(settingsStateSelector, (state) => state.user)
const events = createSelector(eventStateSelector, (state) => state.entities)
const event = createSelector(eventStateSelector, (state) => state.event)
const accounts = createSelector(accountStateSelector, (state) => state.entities)
const singleStoreAccount = createSelector(accountStateSelector, (state) => state.account)
const publicAccounts = createSelector(accountStateSelector, (state) => state.public_info)
const taskLoadingCount = createSelector(tasksStateSelector, (state) => state.loadingCount)

export const tasksReselector = createSelector(tasksStateSelector, (state) => Object.values(state.entities))

export const allTaskColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.allTasks}_columns`]
    // console.log({ selectedColumns, settings })
    return (!selectedColumns ? allDefaultTaskColumns : selectedColumns.filter(c => c.selected).map(c => allTaskColumnsById[c.value]))
  }
)

export const taskColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.tasksTable}_columns`]
    // console.log({ selectedColumns, settings })
    return (!selectedColumns ? defaultTaskColumns : selectedColumns.filter(c => c.selected).map(c => taskColumnsById[c.value]))
  }
)

export const myTaskColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.myTasks}_columns`]
    // console.log({ selectedColumns, settings })
    return (!selectedColumns ? defaultTaskColumns : selectedColumns.filter(c => c.selected).map(c => taskColumnsById[c.value]))
  }
)

interface TaskPreselectorData { user: User, workersByEmail: { [key:string]: WorkerWithProfile }, workersByUId: { [key:string]: WorkerWithProfile }, filterState: FiltersState, accounts: Dictionary<JoiAccount>, events: Dictionary<JoiEvent>, eventTaskLabels: Dictionary<Label>, publicAccounts: Dictionary<AccountPublicInfo> }

export const taskPreselector = createSelector( // cause 10 args is max
  user,
  workersByEmail,
  workersByUId,
  filters,
  accounts,
  publicAccounts,
  events,
  taskLabels,
  (user: User, workersByEmail, workersByUId, filterState: FiltersState, accounts: Dictionary<JoiAccount>, publicAccounts: Dictionary<AccountPublicInfo>, events: Dictionary<JoiEvent>, eventTaskLabels: Dictionary<Label>): number | TaskPreselectorData => {
    return { user, workersByEmail, workersByUId, filterState, accounts, events, eventTaskLabels, publicAccounts }
  }
)

export interface TaskTableReselectorData { taskTableData: TaskTableData[], taskTableRowsById: Dictionary<TaskTableData>, loadingCount?: number }

export const taskTablesReselectorGenerator = (filterPageType: keyof FilterGroupTypes, entityId = null) => createSelector(
  taskPreselector,
  taskLoadingCount,
  tasksReselector,
  event,
  filteredCalendarEventsReselectorGenerator('allTasks'),
  entitiesReselector,
  singleStoreAccount,
  ({ user, workersByEmail, workersByUId, filterState, accounts, publicAccounts, events, eventTaskLabels }: TaskPreselectorData, loadingCount: number, tasks: Task[], event: JoiEvent, filteredEvents, entities, singleStoreAccount): TaskTableReselectorData => {
    
    if (loadingCount || !tasks.length || (filterPageType !== 'allTasks' && !event)) return { taskTableData: [], taskTableRowsById: {}, loadingCount }
    
    if (entityId) {
      tasks = tasks.filter(t => t.entityId === entityId)
    } else {
      const filters = filterState[filterPageType] as FilterGroup
      const toggleFilters = filterState.toggleFilters[filterPageType]
      // console.log({ filterPageType, filters, toggleFilters })
      tasks = filterTasks(tasks, filters, toggleFilters, user)
      // console.log({ filteredTasks: jsonCopyObject(tasks), toggleFilters, filters, user, eventIds: filteredEvents.eventIds })
      if (filterPageType === 'allTasks') {
        tasks = tasks.filter(t => filteredEvents.eventIds.has(t.eventId))
        const { workersByEmail: WBE, workersByUId: WBUID } = getWorkersByEmailAndUidFromMultipleEvents(events)
        workersByEmail = WBE
        workersByUId = WBUID
      } else {
        tasks = tasks.filter(t => t.eventId === event.eventId)
      }
      if (filters.search) {
        const searchTerm = filters.search.toLocaleLowerCase()
        tasks = tasks.filter(t => t.name && t.name.toLocaleLowerCase().includes(searchTerm))
      }
    }

    const { uid } = user
    const now = new Date()
    const labelMapsByEvent: Dictionary<Dictionary<Label>> = {}

    const taskTableData = tasks.map(t => {
      const { flag, comments, id, commentBookmarks, name, dueDate, content, program, eventId, accountId, responsibility, files, groupVisibility, visibility, labels, subtasks, subtasks_obj, seen, createdBy, status } = t
      let account = accounts[accountId] || publicAccounts[accountId]
      if(!account){
        if(singleStoreAccount){
          console.log({ singleStoreAccount })
          account = singleStoreAccount as unknown as JoiAccount
        }
      }
      if (!account) return null
      const creator = workersByUId[createdBy]
      // console.log({ createdBy, creator })
      const nComments = !comments ? 0 : comments.length
      const cBookmarks = commentBookmarks || {}
      const date = !dueDate ? null : str2Date(dueDate)
      const responsible = filterPageType === 'allTasks' ? events[eventId].workforce.find(w => w.email === responsibility) : workersByEmail[responsibility]
      const ev = filterPageType !== 'allTasks' ? event : events[eventId] || {} as JoiEvent
      const programName = ev.programNames[program]
      // console.log({ program, event, names: event.programNames })
      let taskLabels: Dictionary<Label>
      if (filterPageType === 'allTasks') {
        if (!labelMapsByEvent[eventId]) labelMapsByEvent[eventId] = publicHelpers.makeLabelMap(ev.task_labels)
        taskLabels = labelMapsByEvent[eventId]
      } else taskLabels = eventTaskLabels
      
      const allVisible = groupVisibility.includes('--all--')
      const extendedVisible = allVisible && (groupVisibility.length > 1 || visibility?.length > 1)
      const nSubTasks = !subtasks ? 0 : subtasks.length
      const completedSubs = !nSubTasks ? 0 : subtasks.reduce((completed, s) => completed + (subtasks_obj[s].completed ? 1 : 0), 0)
      const completed = status === 'complete'

      const entity = entities[t.entityId]
      const taskRow: TaskTableData = {
        fullTask: t,
        readOnly: account.is_suspended,
        dimmed: completed,
        seen: !seen || !seen[uid] ? 'unseen' : 'seen',
        id: id,
        associatedWith: { text: entity?.name || '' },
        eventId: ev.eventId,
        subTasks: !nSubTasks ? null : {
          percent: completedSubs / nSubTasks,
          lights: [{
            text: `${completedSubs} / ${nSubTasks}`,
            color: completedSubs === nSubTasks ? tableLightColors.green : !completedSubs ? tableLightColors.red  : tableLightColors.yellow,
          }]},
        labels: !labels ? [] : labels.filter(l => taskLabels[l]).map(label => ({ color: taskLabels[label].color, label: taskLabels[label].label })),
        visibility: { text: allVisible ? extendedVisible ? "Extended" : "Program & Logistics Teams" : "Limited", icon: allVisible ? extendedVisible ? "unlocked_eye" : "workforce_24px" : "read_only_24px", rank: allVisible ? extendedVisible ? -1 : 0 : 1 },
        files: !files || !files.length ? null : { files: files.length },
        account: !account ? null : { name: account.title, photoUrl: account.photoURL || null, id: account.id },
        event: { text: ev.title },
        creator: !creator ? null : {
          name: nameJoiner(creator),
          id: creator.uid,
          email: creator.email,
          photoUrl: creator.photoURL,
          fullPhotoUrl: creator.photoURL,
        },
        responsibility: !responsible ? null : { 
          name: nameJoiner(responsible),
          id: responsible.uid || null,
          email: responsible.email,
          photoUrl: responsible.photoURL || null,
          fullPhotoUrl: responsible.photoURL || null,
        },
        program: !programName ? null : { text: programName },
        additionalInfo: !content ? null : { text: content },
        task: { text: name, strikethrough: completed },
        // completed: { on: completed },
        flagged: { flagged: flag },
        commented: { 
          comments: nComments,
          unseenComments: !cBookmarks[uid] ? nComments : nComments - cBookmarks[uid],
        },
        dueBy: !date ? null : { date, HHMMText: `${DayFull[date.getDay()]}, `, red: date < now && !completed },
        status: !t ? null : { task: t }
      }
      return taskRow

    }).filter(t => t)

    const taskTableRowsById = makeMapFromListOfObjectsAndKey(taskTableData, 'id')
    // console.log(`It took ${Date.now() - start}ms to reselect the task data one time (for ${taskTableData.length} tasks)`)
    return { taskTableData: taskTableData, taskTableRowsById }
  }
)

export const taskTimeTableReselector = createSelector(
  tasksReselector,
  user,
  workersByEmail,
  filters,
  (tasks: Task[], user: User, workersByEmail: { [key:string]: WFWorker }, filterState: FiltersState, filterPageType: string): 
  { summary: TimeLogSummary, taskTimeDataRows: TaskTimeTableData[], logs: TimeLogData[] } => {
    // filtering for tasks visible to the current user that have either estimated time and/or logged time 
    const filters = filterState[filterPageType] as FilterGroup
    const toggleFilters = filterState.toggleFilters[filterPageType]
    tasks = filterTasks(filterTimeTasks(tasks, user), { ...filters }, toggleFilters, user)
    const { search } = filters
    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      tasks = tasks.filter(t => t.name.toLocaleLowerCase().includes(searchTerm))
    }

    if (!tasks.length) return { summary: null, taskTimeDataRows: [], logs: [] }
    
    let totalEstimatedMinutes = 0
    let totalLoggedTime = 0
    const logs: TimeLogData[] = []
    const taskTimeDataRows: TaskTimeTableData[] = tasks.map(task => {
      const { estimatedMinutes, loggedTime } = getEstimatedMinutesAndLoggedTime(task) // no need for null checks after this because having one will set the other
      totalEstimatedMinutes += estimatedMinutes
      totalLoggedTime += loggedTime
      const responsibleWorker = !task.responsibility ? null : workersByEmail[task.responsibility]
      const responsibleWorkerData: TableUser = !responsibleWorker ? null : { 
        name: `${responsibleWorker.firstName || ''} ${responsibleWorker.lastName || ''}`,
        id: responsibleWorker.uid,
        email: responsibleWorker.email,
        photoUrl: responsibleWorker.photoURL,
        fullPhotoUrl: responsibleWorker.photoURL,
      }
      const difference = minutesToHHMM(estimatedMinutes - loggedTime)
      const allVisible = task.groupVisibility.includes('--all--')
      const extendedVisible = allVisible && (task.groupVisibility.length > 1 || task.visibility?.length > 1)
      const taskTimeTableRowData: TaskTimeTableData = {
        name: { text: task.name, bold: true },
        id: task.id,
        task,
        responsibility:  responsibleWorkerData,
        estimatedTime:  { time: minutesToHHMM(estimatedMinutes) },
        loggedTime: { time: minutesToHHMM(loggedTime) },
        difference: { time: difference, warning: difference.negative }, 
        visibility: { text: allVisible ? extendedVisible ? "Extended" :"Program & Logistics Teams" : "Limited", icon: allVisible ? extendedVisible ? "unlocked_eye" :  "workforce_24px" : "read_only_24px", rank: allVisible ? extendedVisible ? -1 : 0 : 1 }
      }
      if (task.log) task.log.forEach(l => {
        const date = l.createdAt.hasOwnProperty('seconds') ? new Date(l.createdAt['seconds'] * 1000) : l.createdAt
        const user = workersByEmail[l.createdBy]
        const logData: TimeLogData = {
          fullTask: task,
          task: { text: task.name, bold: true },
          timeLog: l,
          id: l.id,
          loggedBy: !user ? null : { name: user.firstName + ' ' + user.lastName, id: user.uid, email: l.createdBy, photoUrl: user.photoURL || null, fullPhotoUrl: user.photoURL },
          date: { HHMMText: null, date },
          notes: { text: l.content },
          loggedTime: { time: l.loggedTime },
        }
        logs.push(logData)
      })
      return taskTimeTableRowData
    })
    const summary: TimeLogSummary = {
      hasEstimatedTime: true,
      estimatedTime: minutesToHHMM(totalEstimatedMinutes),
      loggedTime: minutesToHHMM(totalLoggedTime),
      difference: minutesToHHMM(totalEstimatedMinutes - totalLoggedTime),
      percentDone: 100 * totalLoggedTime / totalEstimatedMinutes
    }
    return { summary, taskTimeDataRows, logs }
  }
)

const getEstimatedMinutesAndLoggedTime = (task: Task) => {
  const { estimatedTime, log } = task
  let estimatedMinutes: number = null
  if (estimatedTime) {
    estimatedMinutes = estimatedTime.h * 60 + estimatedTime.m
  } else if (log && log.length) {
    estimatedMinutes = 0
  }
  let loggedTime: number = null
  if (log && log.length) {
    loggedTime = 0
    log.forEach(({ loggedTime: { h, m } }) => loggedTime += h*60 + m)
  }
  return { estimatedMinutes, loggedTime }
}

export const timeLogReselector = createSelector(
  tasks,
  workersByEmail,
  (tasks: Dictionary<Task>, workersByEmail: { [key:string]: WFWorker },  taskId): { summary: TimeLogSummary, logs: TimeLogData[] } => {
    const task = tasks[taskId]
    const summary: TimeLogSummary = {}
    // NOTE: logs is the table data, log is the log list from task.  Sorry for the naming.
    const logs: TimeLogData[] = []

    if (!task) return { summary, logs }
    const { estimatedMinutes, loggedTime } = getEstimatedMinutesAndLoggedTime(task)
    if (estimatedMinutes !== null) {
      summary.hasEstimatedTime = true
      summary.estimatedTime = minutesToHHMM(estimatedMinutes)
    }
    if (loggedTime !== null) {
      summary.loggedTime = minutesToHHMM(loggedTime)
    }
    if (estimatedMinutes !== null && loggedTime !== null) {
      const diff = estimatedMinutes - loggedTime
      summary.difference = minutesToHHMM(diff)
      summary.percentDone = 100 * loggedTime / estimatedMinutes
    }

    if (!task.log) return { summary, logs }
    task.log.forEach(l => {
      const date = l.createdAt.hasOwnProperty('seconds') ? new Date(l.createdAt['seconds'] * 1000) : l.createdAt
      const user = workersByEmail[l.createdBy]
      const logData: TimeLogData = {
        fullTask: task,
        timeLog: l,
        id: l.id,
        loggedBy: !user ? null : { name: user.firstName + ' ' + user.lastName, id: user.uid, email: l.createdBy, photoUrl: user.photoURL || null, fullPhotoUrl: user.photoURL },
        date: { HHMMText: null, date },
        notes: { text: l.content },
        loggedTime: { time: l.loggedTime },
      }
      logs.push(logData)
    })
    return { summary, logs }
  }
)

export const tasksReadyReselector = createSelector(
  user,
  permissions,
  myGroupsReselector,
  (user, permissions, myGroupsSet) =>
    !user || !permissions ? {myGroupsSet,permissions:null}  : {myGroupsSet,permissions}
)

export const checkTriggers = createSelector(
  all_triggers,
  focused,
  (all_triggers, focused) => ({all_triggers, focused})
)